import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { Math as MathFormula } from "@/components/Math";
import { CopyResultButton } from "@/components/CopyResultButton";

export default function BufferSolutionCalculator() {
  const [pka, setPka] = useState("");
  const [acidConc, setAcidConc] = useState("");
  const [baseConc, setBaseConc] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculate = () => {
    const pkaValue = parseFloat(pka);
    const acid = parseFloat(acidConc);
    const base = parseFloat(baseConc);

    if (isNaN(pkaValue) || isNaN(acid) || isNaN(base) || acid <= 0 || base <= 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid positive numbers.",
        variant: "destructive",
      });
      return;
    }

    // Henderson-Hasselbalch equation: pH = pKa + log([A-]/[HA])
    const ph = pkaValue + Math.log(base / acid) / Math.LN10;
    setResult(ph);

    toast({
      title: "Calculation Complete",
      description: `Buffer pH: ${ph.toFixed(2)}`,
    });
  };

  const reset = () => {
    setPka("");
    setAcidConc("");
    setBaseConc("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Buffer Solution Calculator" 
      calculatorPath="/calculator/buffer-solution"
      category="chemistry"
      subcategory="Acids & Bases"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Buffer Solution Calculator</CardTitle>
            <CardDescription>
              Calculate buffer pH using Henderson-Hasselbalch equation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pka">pKa of Acid</Label>
                <Input
                  id="pka"
                  type="number"
                  step="any"
                  value={pka}
                  onChange={(e) => setPka(e.target.value)}
                  placeholder="Enter pKa"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="acid">[Acid] Concentration (M)</Label>
                <Input
                  id="acid"
                  type="number"
                  step="any"
                  value={acidConc}
                  onChange={(e) => setAcidConc(e.target.value)}
                  placeholder="Enter acid concentration"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="base">[Base] Concentration (M)</Label>
                <Input
                  id="base"
                  type="number"
                  step="any"
                  value={baseConc}
                  onChange={(e) => setBaseConc(e.target.value)}
                  placeholder="Enter conjugate base concentration"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result !== null && (
              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">Buffer pH</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start justify-between gap-4">
                    <p className="text-2xl font-bold break-all overflow-wrap-anywhere flex-1">{result.toFixed(2)}</p>
                    <CopyResultButton value={result.toFixed(2)} />
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Henderson-Hasselbalch Equation</CardTitle>
              </CardHeader>
              <CardContent>
                <MathFormula block>{String.raw`\text{pH} = \text{p}K_a + \log_{10}\left(\frac{[A^-]}{[HA]}\right)`}</MathFormula>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
